import React, { useState } from 'react';
import { Gift, X, Loader2, PartyPopper, CheckCircle } from 'lucide-react';
import { Button } from '../Button';
import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import confetti from 'canvas-confetti';

interface BirthdayBonusModalProps {
    isOpen: boolean;
    onClose: () => void;
    session: Session | null;
    onSuccess: () => void;
}

export const BirthdayBonusModal: React.FC<BirthdayBonusModalProps> = ({ isOpen, onClose, session, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isClaimed, setIsClaimed] = useState(false);

    if (!isOpen || !session) return null;

    const fireConfetti = () => {
        const duration = 2.5 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

        const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

        const interval: any = setInterval(function () {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);
            confetti(Object.assign({}, defaults, {
                particleCount,
                origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
            }));
            confetti(Object.assign({}, defaults, {
                particleCount,
                origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
            }));
        }, 250);
    };

    const handleClaim = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/.netlify/functions/loyalty-action', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ action: 'birthday_bonus' })
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.message === 'Bônus de aniversário já resgatado este ano') {
                    // If already claimed, let's treat it as success visually so it stops bothering them
                    setIsClaimed(true);
                } else {
                    throw new Error(data.message || 'Erro ao resgatar bônus');
                }
            } else {
                setIsClaimed(true);
                fireConfetti();
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="relative w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-3xl dark:bg-[#121214] border border-white/20 dark:border-gray-800">

                {/* Animated Banner */}
                <div className="h-32 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 relative overflow-hidden flex items-center justify-center">
                    <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay"></div>
                    <Gift className="w-16 h-16 text-white animate-bounce drop-shadow-lg" />
                </div>

                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/20 hover:bg-black/40 rounded-full transition-colors z-10"
                >
                    <X className="w-5 h-5" />
                </button>

                {isClaimed ? (
                    <div className="p-6 text-center animate-fade-in">
                        <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-10 h-10 text-green-500" />
                        </div>
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent mb-2">
                            Bônus Resgatado!
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 font-medium">
                            Os pontos do seu presente de aniversário foram adicionados à sua conta. Aproveite ao máximo! 🎉
                        </p>
                        <Button
                            onClick={onSuccess}
                            className="w-full h-12 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-xl shadow-green-500/20 text-white"
                        >
                            Continuar
                        </Button>
                    </div>
                ) : (
                    <div className="p-6 text-center">
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent flex items-center justify-center gap-2 mb-2">
                            Feliz Aniversário! <PartyPopper className="w-6 h-6 text-orange-500" />
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 font-medium">
                            Estamos no mês do seu aniversário! Como presente, preparamos um bônus especial de pontos para você resgatar. Aproveite!
                        </p>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg">
                                {error}
                            </div>
                        )}

                        <Button
                            onClick={handleClaim}
                            disabled={loading}
                            className="w-full h-12 text-lg font-bold bg-gradient-to-r from-orange-500 to-red-500 shadow-xl shadow-orange-500/20"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                            ) : (
                                'Resgatar Presente'
                            )}
                        </Button>

                        <p className="mt-4 text-xs text-gray-500 dark:text-gray-500">
                            Disponível uma vez por ano, apenas no mês do seu aniversário.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
